import { memo, useState, useCallback } from "react";
import { useI18n } from "@/i18n";
import type { Locale } from "@/i18n";
import { useRouter } from "@/application/stores/routerStore";
import { colors, typography, transitions } from "@/design-system/tokens";

type DocSection = "warehouses" | "locations" | "products" | "typeUse" | "typeUnit" | "movementSubtypes" | "unitConversions" | "stock" | "receipts" | "dispatches" | "transfers" | "adjustments" | "balance" | "kardex";

interface SidebarGroup {
  label: string;
  labelKey: string;
  items: DocSection[];
}

const SIDEBAR_GROUPS: SidebarGroup[] = [
  { label: "Modules", labelKey: "modules", items: ["warehouses", "locations", "products"] },
  { label: "Inventory", labelKey: "inventory", items: ["stock", "receipts", "dispatches", "transfers", "adjustments", "balance", "kardex"] },
  { label: "Settings", labelKey: "settings", items: ["typeUse", "typeUnit", "movementSubtypes", "unitConversions"] },
];

export const DocsPage = memo(function DocsPage() {
  const { t, locale, setLocale } = useI18n();
  const { navigate } = useRouter();
  const [activeSection, setActiveSection] = useState<DocSection>("warehouses");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleNav = useCallback((section: DocSection) => {
    setActiveSection(section);
    setSidebarOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const docs = t.docs;

  return (
    <div style={{ minHeight: "100vh", background: "#fafafa", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <header style={{ background: "#fff", borderBottom: "1px solid #eee", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button
            onClick={() => setSidebarOpen(v => !v)}
            className="docs-mobile-toggle"
            style={{ display: "none", background: "none", border: "none", cursor: "pointer", color: "#555", padding: "4px" }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12h18M3 6h18M3 18h18" /></svg>
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "30px", height: "30px", background: colors.primary, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="15" height="15" viewBox="0 0 20 20" fill="none"><path d="M3 7L10 3L17 7V13L10 17L3 13V7Z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" /><path d="M10 3V17M3 7L17 13M17 7L3 13" stroke="white" strokeWidth="1.1" strokeOpacity="0.55" /></svg>
            </div>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#111" }}>Nexo Warehouse</span>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#999", marginLeft: "-4px" }}>{docs.title}</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={() => navigate("landing")}
            style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#666", background: "none", border: "none", cursor: "pointer", padding: "6px 12px", borderRadius: "8px", transition: `color ${transitions.fast}` }}
            onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.color = "#111")}
            onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.color = "#666")}
          >
            ← {docs.backHome}
          </button>
          <div role="group" aria-label="Language selector" style={{ display: "flex", background: "#f0f0f0", borderRadius: "9999px", padding: "3px", gap: "2px" }}>
            {(["es", "en"] as const).map(l => (
              <button key={l} onClick={() => setLocale(l)} style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.bold, padding: "3px 9px", borderRadius: "9999px", border: "none", cursor: "pointer", transition: `all ${transitions.fast}`, background: locale === l ? "#111" : "transparent", color: locale === l ? "#fff" : "#777", letterSpacing: "0.05em", lineHeight: 1.6 }}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div style={{ display: "flex", flex: 1 }}>
        {/* Sidebar */}
        <aside
          className={`docs-sidebar ${sidebarOpen ? "open" : ""}`}
          style={{
            width: "240px",
            background: "#fff",
            borderRight: "1px solid #eee",
            padding: "24px 16px",
            position: "sticky",
            top: "57px",
            height: "calc(100vh - 57px)",
            overflowY: "auto",
            flexShrink: 0,
          }}
        >
          <nav style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {SIDEBAR_GROUPS.map(group => (
              <div key={group.labelKey} style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                <span style={{ fontFamily: typography.fontFamily, fontSize: "10px", fontWeight: typography.weight.bold, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.1em", padding: "8px 12px 4px" }}>{docs.groups?.[group.labelKey] ?? group.label}</span>
                {group.items.map(section => (
                  <button
                    key={section}
                    onClick={() => handleNav(section)}
                    style={{
                      fontFamily: typography.fontFamily,
                      fontSize: typography.size.sm,
                      fontWeight: activeSection === section ? typography.weight.semibold : typography.weight.medium,
                      color: activeSection === section ? colors.primary : "#555",
                      background: activeSection === section ? "rgba(235,81,57,0.06)" : "transparent",
                      border: "none",
                      borderRadius: "8px",
                      padding: "10px 12px",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: `all ${transitions.fast}`,
                    }}
                  >
                    {docs.sections[section].title}
                  </button>
                ))}
              </div>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main style={{ flex: 1, padding: "40px 48px 80px", maxWidth: "780px" }} className="docs-content">
          <DocContent section={activeSection} docs={docs} />
        </main>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .docs-mobile-toggle { display: flex !important; }
          .docs-sidebar { 
            position: fixed !important; 
            top: 57px !important; 
            left: 0; 
            bottom: 0; 
            z-index: 40; 
            transform: translateX(-100%); 
            transition: transform 0.2s ease;
            box-shadow: 4px 0 16px rgba(0,0,0,0.1);
          }
          .docs-sidebar.open { transform: translateX(0); }
          .docs-content { padding: 24px 20px 60px !important; }
        }
      `}</style>
    </div>
  );
});

const DocContent = memo(function DocContent({ section, docs }: { section: DocSection; docs: any }) {
  const data = docs.sections[section];
  return (
    <article style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      <div>
        <h1 style={{ fontFamily: typography.fontFamily, fontSize: "2rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, letterSpacing: "-0.02em" }}>{data.title}</h1>
        <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, color: "#666", marginTop: "8px", lineHeight: "1.6" }}>{data.intro}</p>
      </div>

      {data.blocks.map((block: any, i: number) => (
        <DocBlock key={i} block={block} />
      ))}
    </article>
  );
});

function DocBlock({ block }: { block: any }) {
  if (block.type === "heading") {
    return <h2 style={{ fontFamily: typography.fontFamily, fontSize: "1.3rem", fontWeight: typography.weight.bold, color: "#111", margin: 0, paddingTop: "8px", borderTop: "1px solid #f0f0f0" }}>{block.text}</h2>;
  }
  if (block.type === "paragraph") {
    return <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555", lineHeight: "1.7", margin: 0 }}>{block.text}</p>;
  }
  if (block.type === "steps") {
    return (
      <ol style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#444", lineHeight: "1.8", margin: 0, paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "6px" }}>
        {block.items.map((step: string, i: number) => <li key={i}>{step}</li>)}
      </ol>
    );
  }
  if (block.type === "note") {
    return (
      <div style={{ background: "#f5f5f5", border: "1px solid #e8e8e8", borderRadius: "10px", padding: "14px 18px", borderLeft: "3px solid #999" }}>
        <p style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#555", margin: 0, lineHeight: "1.5" }}>{block.text}</p>
      </div>
    );
  }
  if (block.type === "table") {
    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: typography.fontFamily, fontSize: typography.size.sm }}>
          <thead>
            <tr>
              {block.headers.map((h: string, i: number) => (
                <th key={i} style={{ textAlign: "left", padding: "10px 14px", borderBottom: "2px solid #eee", color: "#888", fontSize: "11px", fontWeight: typography.weight.bold, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row: string[], ri: number) => (
              <tr key={ri}>
                {row.map((cell: string, ci: number) => (
                  <td key={ci} style={{ padding: "10px 14px", borderBottom: "1px solid #f5f5f5", color: "#444" }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.type === "list") {
    return (
      <ul style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#444", lineHeight: "1.8", margin: 0, paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "4px" }}>
        {block.items.map((item: string, i: number) => <li key={i}>{item}</li>)}
      </ul>
    );
  }
  return null;
}
